import React from "react";

/**
 * AboutPage: Company story, mission, and solution overview
 * Static content page with no state management
 */
const AboutPage: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="section-padding gradient-hero">
        <div className="container-max text-center">
          <div className="animate-fade-in">
            <h1 className="heading-1 mb-6">About ReadyToClaim</h1>
            <p className="body-large max-w-3xl mx-auto">
              Born from the devastating impact of recent natural disasters, we're dedicated to helping 
              homeowners protect what matters most through comprehensive inventory and valuation services.
            </p>
          </div>
        </div>
      </section>

      {/* Why We Started */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="heading-2 mb-6">Why We Started</h2>
            <p className="body-large max-w-3xl mx-auto">
              Recent hurricanes like Helene in Florida have shown us the critical need for proper 
              documentation when disaster strikes.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="animate-fade-in">
              <h3 className="heading-3 mb-6">The Problem We Saw</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Devastating Losses</h4>
                    <p className="text-gray-600">Hurricanes like Helene left thousands of Florida families without proper documentation of their belongings, making insurance claims nearly impossible.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">No Easy Solutions</h4>
                    <p className="text-gray-600">Existing methods for documenting home inventories were either too complex, too expensive, or simply didn't work for disaster scenarios.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Financial Devastation</h4>
                    <p className="text-gray-600">Families lost everything and received minimal compensation because they couldn't prove what they owned or its value.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="animate-fade-in" style={{animationDelay: '0.2s'}}>
              <div className="relative">
                <img 
                  src="/images/front.jpg" 
                  alt="Home affected by natural disaster" 
                  className="w-full rounded-3xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent rounded-3xl"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-xl font-bold text-white mb-2">Recent Impact</h3>
                  <p className="text-white/90 text-sm">Hurricane Helene and other recent disasters highlighted the urgent need for better preparation</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Solution */}
      <section className="section-padding gradient-primary">
        <div className="container-max">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="heading-2 mb-6">Our Solution</h2>
            <p className="body-large max-w-3xl mx-auto">
              We created a comprehensive service that combines professional expertise with 
              modern technology to ensure every homeowner is prepared for the unexpected.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card p-8 text-center hover-lift animate-fade-in">
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="heading-3 mb-4">Professional Documentation</h3>
              <p className="body-medium">
                Expert specialists conduct comprehensive video calls to document every item in your home, 
                ensuring nothing is missed when disaster strikes.
              </p>
            </div>

            <div className="card p-8 text-center hover-lift animate-fade-in" style={{animationDelay: '0.1s'}}>
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="heading-3 mb-4">Accurate Valuations</h3>
              <p className="body-medium">
                Professional analysis provides accurate market valuations for each item, 
                ensuring fair compensation from insurance companies.
              </p>
            </div>

            <div className="card p-8 text-center hover-lift animate-fade-in" style={{animationDelay: '0.2s'}}>
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="heading-3 mb-4">Insurance Ready</h3>
              <p className="body-medium">
                All documentation is formatted to meet insurance company requirements, 
                making the claims process smooth and efficient.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="heading-2 mb-6">Our Mission</h2>
            <p className="body-large max-w-3xl mx-auto">
              To ensure no family ever loses everything without proper compensation again. 
              We believe every homeowner deserves peace of mind and financial protection.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="card p-8 hover-lift animate-fade-in">
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="heading-3 mb-4">Protect What Matters</h3>
              <p className="body-medium">
                We help families protect their most valuable possessions by creating comprehensive 
                documentation that stands up to insurance company scrutiny.
              </p>
            </div>

            <div className="card p-8 hover-lift animate-fade-in" style={{animationDelay: '0.1s'}}>
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="heading-3 mb-4">Speed & Reliability</h3>
              <p className="body-medium">
                Our process is designed to be fast, reliable, and accessible to everyone, 
                especially those in disaster-prone areas who need it most.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-padding bg-primary-600 relative overflow-hidden">
        <div className="container-max text-center relative z-10">
          <div className="animate-scale-in">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Protect Your Belongings?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              Join the first 25 users and get started for free
            </p>
            <a href="/try" className="btn-secondary inline-block">
              Get Started Now
            </a>
          </div>
        </div>

        {/* Background Pattern */}
        <div className="absolute inset-0 z-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage; 